// src/Pages/Admin/Team/TeamDashboard.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Badge,
    Button,
    Card,
    Center,
    Collapse,
    Divider,
    Group,
    Loader,
    Paper,
    Progress,
    SimpleGrid,
    Stack,
    Text,
    ThemeIcon,
    UnstyledButton,
} from "@mantine/core";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FaBed, FaIdCard, FaPlane, FaDumbbell } from "react-icons/fa";
import { FaUserCheck } from "react-icons/fa6";

import { useLanguage } from "../../../context/LanguageContext";
import "../../Pages.css";
import Billede1 from "../../../Images/Billede1.jpg";
import HeroSectionV2 from "../../../components/HeroSectionV2";
//import { teamModuleConfig } from "../../../Helper/teamModuleConfig";

type SettingType = "bit" | "int" | "string" | "datetime";

interface EventSetting {
    settingKey: string;
    settingValue: string;
    settingType: SettingType;
}

interface EventDto {
    eventID: number;
    eventName: string;
    active: boolean;
    eventSettings: EventSetting[];
}

function buildSettingsMap(eventSettings: EventSetting[] | undefined) {
    const map: Record<string, EventSetting> = {};
    for (const s of eventSettings ?? []) {
        map[s.settingKey] = s;
    }
    return map;
}

function getBit(settings: Record<string, EventSetting>, key: string): boolean {
    const setting = settings[key];
    if (!setting) return false;

    const value = setting.settingValue?.toLowerCase?.();
    return setting.settingValue === "1" || value === "true";
}

function isExpired(deadlineDate?: string): boolean {
    if (!deadlineDate) return false;

    const deadline = new Date(deadlineDate);

    if (isNaN(deadline.getTime())) {
        return false;
    }

    return new Date() > deadline;
}

const RED = "#ff5e5e";
const DARK_BG = "white";
const DARK_CARD = "#1e1e1e";
const DARK_BORDER = "rgba(255,255,255,0.08)";
const MUTED = "rgba(255,255,255,0.7)";

const TeamDashboard: React.FC = () => {
    const { language } = useLanguage();
    const { eventId } = useParams<{ eventId: string }>();
    const navigate = useNavigate();

    const id = useMemo(() => Number(eventId), [eventId]);
    const userId = useMemo(() => sessionStorage.getItem("userId") ?? "", []);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [eventData, setEventData] = useState<EventDto | null>(null);
    const [openKey, setOpenKey] = useState<string | null>(null);

    useEffect(() => {
        if (!id || Number.isNaN(id)) {
            setError("Ugyldigt eventId i URL");
            setLoading(false);
            return;
        }

        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) {
            setError("API URL mangler i miljøvariabler.");
            setLoading(false);
            return;
        }

        let isMounted = true;
        const controller = new AbortController();

        const loadEvent = async () => {
            try {
                setLoading(true);
                setError("");

                const url = `${apiUrl}/api/events/${id}/dashboard`;


                const response = await fetch(url, {
                    method: "GET",
                    signal: controller.signal,
                    headers: {
                        "Content-Type": "application/json",
                    },
                });



                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const data: EventDto = await response.json();


                if (isMounted) {
                    setEventData(data);
                }
            } catch (err: any) {
                console.error("TeamDashboard load error:", err);

                if (err?.name === "AbortError") {
                    console.warn("Fetch aborted");
                    return;
                }

                if (isMounted) {
                    setError("Kunne ikke hente event settings.");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                    console.timeEnd("TeamDashboard total load");
                }
            }
        };

        loadEvent();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [id]);

    const settings = useMemo(
        () => buildSettingsMap(eventData?.eventSettings),
        [eventData?.eventSettings]
    );

    const getDeadlineLabel = (deadline?: string) => {
        if (!deadline) return "No deadline";

        const now = new Date();
        const end = new Date(deadline);

        if (isNaN(end.getTime())) return "Invalid deadline";

        const diffMs = end.getTime() - now.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        const formatted = end.toLocaleString("da-DK", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });

        if (diffDays < 0) {
            return `Deadline: ${formatted} (Expired)`;
        }

        if (diffDays === 0) {
            return `Deadline: ${formatted} (Today)`;
        }

        return `Deadline: ${formatted} (${diffDays} days left)`;
    };

    type TeamModule = {
        key: string;
        titleEn: string;
        titleDa: string;
        shortEn: string;
        shortDa: string;
        longEn: string;
        longDa: string;
        icon: any;
        buildLink: (eventId: number, userId: string) => string;
    };

    const dashboardData = useMemo(() => {
        const FORM_MODULES: TeamModule[] = [
            {
                key: "Accreditations",
                titleEn: "Accreditations",
                titleDa: "Akkrediteringer",
                shortEn: "Register team members for accreditation.",
                shortDa: "Registrér holdmedlemmer til akkreditering.",
                longEn:
                    "Use this module to create and manage accreditations for players, coaches and staff. This module is always open until the final deadline.",
                longDa:
                    "Brug dette modul til at oprette og håndtere akkrediteringer for spillere, trænere og staff. Dette modul er altid åbent frem til den endelige deadline.",
                icon: FaUserCheck,
                buildLink: (eventId: number, userId: string) =>
                    `/TeamDashboard/Accreditations/${eventId}/${userId}`,
            },
            {
                key: "Practice",
                titleEn: "Practice",
                titleDa: "Træning",
                shortEn: "Practice schedule and booking.",
                shortDa: "Træningstider og booking.",
                longEn:
                    "Use this module to manage practice time requests and allocations.",
                longDa:
                    "Brug dette modul til at håndtere ønsker til træningstider og fordeling.",
                icon: FaDumbbell,
                buildLink: (eventId: number, userId: string) =>
                    `/TeamDashboard/Practice/${eventId}/${userId}`,
            },
            {
                key: "Visa",
                titleEn: "Visa",
                titleDa: "Visa",
                shortEn: "Visa support and invitation letter request.",
                shortDa: "Visa-support og anmodning om invitationsbrev.",
                longEn:
                    "Use this module to request visa support. You will be asked to provide personal details and upload any required documents.",
                longDa:
                    "Brug dette modul til at anmode om visa-support. Her indtaster du oplysninger og uploader evt. dokumentation.",
                icon: FaIdCard,
                buildLink: (eventId: number, userId: string) =>
                    `/TeamDashboard/Visa/${eventId}/${userId}`,
            },
            {
                key: "Transport",
                titleEn: "Transport",
                titleDa: "Transport",
                shortEn: "Transport requests between venues and hubs.",
                shortDa: "Transport-forespørgsler mellem venues og hubs.",
                longEn:
                    "Use this module to submit transport needs (airport/station, venue-to-venue etc.).",
                longDa:
                    "Brug dette modul til at indsende transportbehov (lufthavn/station, venue-til-venue osv.).",
                icon: FaPlane,
                buildLink: (eventId: number, userId: string) =>
                    `/TeamDashboard/transport/booking/${eventId}/${userId}`,
            },
            {
                key: "Hotel",
                titleEn: "Accommodation",
                titleDa: "Indkvartering",
                shortEn: "Hotel booking information and requests.",
                shortDa: "Hotelbookinger og overnatning.",
                longEn:
                    "Use this module to handle accommodation requests and confirm hotel bookings.",
                longDa:
                    "Brug dette modul til indkvartering: forespørgsler og bekræftelser af hotelbookinger.",
                icon: FaBed,
                buildLink: (eventId: number, userId: string) =>
                    `/TeamDashboard/hotel/booking/${eventId}/${userId}`,
            },
        ];

        const modules = FORM_MODULES.map((m) => {
            const enabledFromSetting = getBit(settings, `Form_${m.key}`);

            const deadlineRaw =
                settings[`Form_${m.key}_Deadline`]?.settingValue || "";

            const expired = isExpired(deadlineRaw);

            const enabled =
                Boolean(eventData?.active) &&
                enabledFromSetting &&
                !expired;

            return {
                key: `Form_${m.key}`,
                title: language === "en" ? m.titleEn : m.titleDa,
                short: language === "en" ? m.shortEn : m.shortDa,
                long: language === "en" ? m.longEn : m.longDa,
                deadline: deadlineRaw,
                icon: m.icon,
                enabled,
                expired,
                to: m.buildLink(id, userId),
            };
        });

        const openModules = modules.filter((m) => m.enabled && !m.expired).length;
        const expiredModules = modules.filter((m) => m.expired).length;
        const closedModules = modules.filter((m) => !m.enabled && !m.expired).length;
        const enabledCount = modules.filter((m) => m.enabled).length;
        const total = modules.length;

        return {
            orderedModules: [
                ...modules.filter((m) => m.enabled),
                ...modules.filter((m) => !m.enabled),
            ],
            enabledCount,
            total,
            percent: total ? Math.round((enabledCount / total) * 100) : 0,
            openPercent: total ? (openModules / total) * 100 : 0,
            expiredPercent: total ? (expiredModules / total) * 100 : 0,
            closedPercent: total ? (closedModules / total) * 100 : 0,
        };
    }, [settings, eventData?.active, id, language]);

    const {
        orderedModules,
        enabledCount,
        total,
        percent,
        openPercent,
        expiredPercent,
        closedPercent,
    } = dashboardData;

    return (
        <div className="home" style={{ background: DARK_BG, minHeight: "100vh" }}>
            <HeroSectionV2
                titleEn="Team booking"
                titleDa="Team booking"
                descriptionEn={eventData ? `Event: ${eventData.eventName}` : "Loading..."}
                descriptionDa={eventData ? `Event: ${eventData.eventName}` : "Indlæser..."}
                imageSrc={Billede1}
                badgeText="TEAM"

            />

            <section className="events-section">
                {!loading && !error && eventData && (
                    <Paper
                        withBorder
                        radius="md"
                        p="md"
                        style={{
                            background: DARK_CARD,
                            borderColor: DARK_BORDER,
                            marginTop: 16,
                        }}
                    >
                        <Group justify="space-between" align="center">
                            <Group gap="md" align="center">

                                <div>
                                    <Text fw={800} style={{ color: "white" }}>
                                        {eventData.eventName}
                                    </Text>
                                    <Text size="sm" style={{ color: MUTED }}>
                                        {language === "en"
                                            ? "Select a module below to continue with bookings."
                                            : "Vælg et modul nedenfor for at fortsætte med bookings."}
                                    </Text>
                                </div>
                            </Group>

                            <Group gap="xs">
                                <Badge variant="light" color={eventData.active ? "green" : "red"}>
                                    {eventData.active ? "Active" : "Inactive"}
                                </Badge>

                                <Badge
                                    variant="light"
                                    style={{
                                        background: "rgba(227,27,35,0.12)",
                                        color: RED,
                                    }}
                                >
                                    {enabledCount}/{total} {language === "en" ? "enabled" : "aktive"}
                                </Badge>
                            </Group>
                        </Group>

                        <Group justify="space-between" mt="sm">
                            <Text size="sm" style={{ color: MUTED }}>
                                {language === "en" ? "Module availability" : "Modul-tilgængelighed"}
                            </Text>
                            <Text size="sm" style={{ color: MUTED }}>
                                {percent}%
                            </Text>
                        </Group>

                        <Paper p={4} radius="xl" style={{ background: "rgba(255,255,255,0.08)" }}>
                            <Progress.Root size="lg" radius="xl">
                                <Progress.Section value={openPercent} color="green" />
                                <Progress.Section value={expiredPercent} color="yellow" />
                                <Progress.Section value={closedPercent} color="gray" />
                            </Progress.Root>
                        </Paper>
                    </Paper>
                )}

                {loading && (
                    <Center mt="md">
                        <Loader />
                    </Center>
                )}

                {!!error && (
                    <Paper p="md" mt="md" withBorder radius="md">
                        <Text c="red">{error}</Text>
                    </Paper>
                )}

                {!loading && !error && eventData && (
                    <Stack mt="md" gap="md">
                        <Divider
                            my="xs"
                            label={language === "en" ? "Booking modules" : "Bookingmoduler"}
                            labelPosition="center"
                            styles={{
                                label: { color: MUTED },
                                root: { borderColor: "rgba(255,255,255,0.12)" },
                            }}
                        />

                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                            {orderedModules.map((m) => {
                                const Icon = m.icon;
                                const isOpen = openKey === m.key;

                                return (
                                    <Card
                                        key={m.key}
                                        withBorder
                                        radius="md"
                                        style={{
                                            position: "relative",
                                            background: DARK_CARD,
                                            borderColor: m.enabled
                                                ? "rgba(227,27,35,0.45)"
                                                : DARK_BORDER,
                                            boxShadow: m.enabled
                                                ? "0 12px 30px rgba(227,27,35,0.08)"
                                                : "none",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <div
                                            style={{
                                                height: 3,
                                                background: m.enabled ? RED : "rgba(255,255,255,0.10)",
                                                width: "100%",
                                            }}
                                        />

                                        <Group justify="space-between" align="flex-start" mt="md">
                                            <Group>
                                                <ThemeIcon
                                                    size="lg"
                                                    radius="md"
                                                    variant="light"
                                                    style={{
                                                        background: "rgba(227,27,35,0.14)",
                                                        color: RED,
                                                    }}
                                                >
                                                    {Icon && <Icon size={18} />}
                                                </ThemeIcon>

                                                <div>
                                                    <Text fw={800} style={{ color: "white" }}>
                                                        {m.title}
                                                    </Text>
                                                    <Text size="sm" style={{ color: MUTED }}>
                                                        {m.short}
                                                    </Text>
                                                </div>
                                            </Group>

                                            <Badge
                                                variant="light"
                                                color={
                                                    m.expired
                                                        ? "yellow"
                                                        : m.enabled
                                                            ? "green"
                                                            : "gray"
                                                }
                                            >
                                                {m.expired
                                                    ? language === "en"
                                                        ? "Expired"
                                                        : "Udløbet"
                                                    : m.enabled
                                                        ? language === "en"
                                                            ? "Enabled"
                                                            : "Aktiv"
                                                        : language === "en"
                                                            ? "Disabled"
                                                            : "Slået fra"}
                                            </Badge>
                                        </Group>

                                        <Paper
                                            mt="sm"
                                            p="sm"
                                            radius="md"
                                            style={{
                                                background: "rgba(0,0,0,0.22)",
                                                border: "1px solid rgba(255,255,255,0.08)",
                                            }}
                                        >
                                            <Text size="sm" style={{ color: MUTED }}>
                                                <span style={{ color: RED, fontWeight: 800 }}>•</span>{" "}
                                                {getDeadlineLabel(m.deadline)}
                                            </Text>
                                        </Paper>

                                        <Group mt="sm" gap="sm">
                                            <Button
                                                fullWidth
                                                disabled={!m.enabled}
                                                onClick={() => navigate(m.to)}
                                            >
                                                {language === "en" ? "Open" : "Åbn"}
                                            </Button>
                                        </Group>

                                        <UnstyledButton
                                            onClick={() => setOpenKey((prev) => (prev === m.key ? null : m.key))}
                                            style={{
                                                width: "100%",
                                                marginTop: 10,
                                                padding: "8px 10px",
                                                borderRadius: 8,
                                                background: "rgba(255,255,255,0.06)",
                                                border: "1px solid rgba(255,255,255,0.08)",
                                            }}
                                        >
                                            <Group justify="space-between" align="center">
                                                <Text size="sm" style={{ color: "white", fontWeight: 700 }}>
                                                    {language === "en" ? "Read more" : "Læs mere"}
                                                </Text>

                                                {isOpen ? (
                                                    <FaChevronUp color="white" size={14} />
                                                ) : (
                                                    <FaChevronDown color="white" size={14} />
                                                )}
                                            </Group>
                                        </UnstyledButton>

                                        <Collapse in={isOpen}>
                                            <Paper
                                                mt="sm"
                                                p="sm"
                                                radius="md"
                                                style={{
                                                    background: "rgba(0,0,0,0.18)",
                                                    border: "1px solid rgba(255,255,255,0.08)",
                                                }}
                                            >
                                                <Text size="sm" style={{ color: MUTED }}>
                                                    {m.long}
                                                </Text>
                                            </Paper>
                                        </Collapse>
                                    </Card>
                                );
                            })}
                        </SimpleGrid>
                    </Stack>
                )}
            </section>
        </div>
    );
};

export default TeamDashboard;